import React, { useState, useEffect } from 'react';
import { Xmark, Search, User, Calendar, Page } from 'iconoir-react';
import { BoardMember } from '../services/api';

interface Label {
  name: string;
  color: string;
}

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  members: BoardMember[];
  labels: Label[];
  onFilterChange: (filters: FilterState) => void;
}

export interface FilterState {
  keyword: string;
  authorIds: number[];
  memberIds: number[];
  labelNames: string[];
  createdAfter: string;
  createdBefore: string;
  updatedAfter: string;
  updatedBefore: string;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ 
  isOpen, 
  onClose, 
  members, 
  labels,
  onFilterChange 
}) => {
  const [filters, setFilters] = useState<FilterState>({
    keyword: '',
    authorIds: [],
    memberIds: [],
    labelNames: [],
    createdAfter: '',
    createdBefore: '',
    updatedAfter: '',
    updatedBefore: '',
  });

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleReset = () => {
    const emptyFilters: FilterState = {
      keyword: '',
      authorIds: [],
      memberIds: [],
      labelNames: [],
      createdAfter: '',
      createdBefore: '',
      updatedAfter: '',
      updatedBefore: '',
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const toggleMember = (memberId: number) => {
    const newMemberIds = filters.memberIds.includes(memberId)
      ? filters.memberIds.filter(id => id !== memberId)
      : [...filters.memberIds, memberId];
    handleFilterChange({ memberIds: newMemberIds });
  };

  const toggleLabel = (labelName: string) => {
    const newLabelNames = filters.labelNames.includes(labelName)
      ? filters.labelNames.filter(name => name !== labelName)
      : [...filters.labelNames, labelName];
    handleFilterChange({ labelNames: newLabelNames });
  };

  const hasActiveFilters = 
    filters.keyword ||
    filters.authorIds.length > 0 ||
    filters.memberIds.length > 0 ||
    filters.labelNames.length > 0 ||
    filters.createdAfter ||
    filters.createdBefore ||
    filters.updatedAfter ||
    filters.updatedBefore;

  if (!isOpen) return null;

  return (
    <>
      <div className="filter-panel-overlay" onClick={onClose} />
      <div className="filter-panel">
        <div className="filter-panel-header">
          <h3>Filtrer les cartes</h3>
          <button className="btn-icon" onClick={onClose} title="Fermer">
            <Xmark width={20} height={20} strokeWidth={2} />
          </button>
        </div>

        <div className="filter-panel-content">
          {/* Mot-clé */}
          <div className="filter-section">
            <label className="filter-label">
              <Search width={16} height={16} strokeWidth={2} />
              <span>Mot-clé</span>
            </label>
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) => handleFilterChange({ keyword: e.target.value })}
              placeholder="Titre, description, membres, étiquettes..."
              className="filter-input"
            />
          </div>

          {/* Membres affectés */}
          <div className="filter-section">
            <label className="filter-label">
              <User width={16} height={16} strokeWidth={2} />
              <span>Membres affectés</span>
            </label>
            <div className="filter-options">
              {members.map(member => (
                <div
                  key={member.id}
                  className={`filter-option ${filters.memberIds.includes(member.id) ? 'active' : ''}`}
                  onClick={() => toggleMember(member.id)}
                >
                  <div className="filter-option-avatar">
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt={member.name} />
                    ) : (
                      member.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <span>{member.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Étiquettes */}
          <div className="filter-section">
            <label className="filter-label">
              <Page width={16} height={16} strokeWidth={2} />
              <span>Étiquettes</span>
            </label>
            <div className="filter-options">
              {labels.length > 0 ? (
                labels.map((label, index) => (
                  <div
                    key={`${label.name}-${index}`}
                    className={`filter-option filter-label-option ${filters.labelNames.includes(label.name) ? 'active' : ''}`}
                    onClick={() => toggleLabel(label.name)}
                  >
                    <div 
                      className="filter-label-color" 
                      style={{ backgroundColor: label.color }}
                    />
                    <span>{label.name}</span>
                  </div>
                ))
              ) : (
                <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', margin: '8px 0' }}>
                  Aucune étiquette dans ce tableau
                </p>
              )}
            </div>
          </div>

          {/* Date de création */}
          <div className="filter-section">
            <label className="filter-label">
              <Calendar width={16} height={16} strokeWidth={2} />
              <span>Date de création</span>
            </label>
            <div className="filter-date-range">
              <input
                type="date"
                value={filters.createdAfter}
                onChange={(e) => handleFilterChange({ createdAfter: e.target.value })}
                className="filter-input"
                placeholder="Du"
              />
              <span>à</span>
              <input
                type="date"
                value={filters.createdBefore}
                onChange={(e) => handleFilterChange({ createdBefore: e.target.value })}
                className="filter-input"
                placeholder="Au"
              />
            </div>
          </div>

          {/* Date de dernière activité */}
          <div className="filter-section">
            <label className="filter-label">
              <Calendar width={16} height={16} strokeWidth={2} />
              <span>Dernière activité</span>
            </label>
            <div className="filter-date-range">
              <input
                type="date"
                value={filters.updatedAfter}
                onChange={(e) => handleFilterChange({ updatedAfter: e.target.value })}
                className="filter-input"
                placeholder="Du"
              />
              <span>à</span>
              <input
                type="date"
                value={filters.updatedBefore}
                onChange={(e) => handleFilterChange({ updatedBefore: e.target.value })}
                className="filter-input"
                placeholder="Au"
              />
            </div>
          </div>
        </div>

        <div className="filter-panel-footer">
          {hasActiveFilters && (
            <button className="btn btn-secondary" onClick={handleReset}>
              Réinitialiser
            </button>
          )}
        </div>
      </div>
    </>
  );
};
